import { useState, useEffect } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'
import { useMapRefresh } from './context/use-map-refresh'

/**
 * Hook for fetching plot organization polygons for a given farm
 * Returns plots with their point data and organization names
 * Automatically refetches when plots are created/updated via MapRefreshContext
 *
 * @param {string} farmId - UUID of the farm organization
 * @returns {Object} { plots: [...], loading, error, invalidate }
 */
export const usePlotPolygons = (farmId) => {
  const [plots, setPlots] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { refreshTrigger } = useMapRefresh()

  useEffect(() => {
    async function fetchPlotOrganizationPolygons() {
      if (!farmId) {
        setPlots([])
        setLoading(false)
        setError(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Step 1: Fetch plot IDs for farm
        const { data: plotOrgs, error: plotError } = await supabaseGetter.organizations.getPlots(farmId)
        if (plotError) throw plotError

        const plotIds = plotOrgs.map(p => p.id)
        if (plotIds.length === 0) {
          setPlots([])
          return
        }

        // Step 2: Fetch points for plots
        const { data: pointsData, error: pointsError } = await supabaseGetter.organizationPolygons.getByOrganizations(plotIds)
        if (pointsError) throw pointsError

        // Log any plots without points data
        const plotsWithoutPoints = plotIds.filter(id => !pointsData.find(p => p.organization_id === id))
        if (plotsWithoutPoints.length > 0) {
          console.warn(`Found ${plotsWithoutPoints.length} plot(s) without points data:`, plotsWithoutPoints)
        }

        // Step 3: Fetch org names
        const { data: orgsData, error: orgsError } = await supabaseGetter.organizations.getByIds(plotIds)
        if (orgsError) throw orgsError

        // Step 4: Merge data and filter out invalid entries
        const plotsWithNames = pointsData
          .filter(point => point.points && Array.isArray(point.points) && point.points.length >= 3)
          .map(point => {
            const org = orgsData.find(o => o.id === point.organization_id)
            return {
              id: point.organization_id,
              name: org?.name || 'Unnamed Plot',
              points: point.points // Already in {x, y} format
            }
          })

        setPlots(plotsWithNames)
      } catch (err) {
        console.error('Error fetching plot organization organization polygons:', err)
        setError(err.message)
        setPlots([])
      } finally {
        setLoading(false)
      }
    }

    fetchPlotOrganizationPolygons()
  }, [farmId, refreshTrigger])

  return {
    plots,
    loading,
    error,
    invalidate: () => {
      supabaseGetter.organizations.invalidate()
      supabaseGetter.organizationPolygons.invalidate()
    }
  }
}
